import { IFilter } from '@/api/interfaces/IFilter'
import {
  FullScreenBackdrop,
  useDialogStore,
} from '../dialog/FullScreenBackDrop'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useReplaceQuery } from '@/util/router'
import classNames from 'classnames'
import { produce } from 'immer'
import { WithT } from '@/locales/types'
import { createT } from '@/locales/index'
import DateInput, { DateSelectType } from '../form/DateInput'
import dayjs from 'dayjs'
import { FilterType } from '@/api/types/channel'
import { Icon } from '../Icon'
import { ChannelType } from '@/api/types/channel'
import { ImageWithFallback } from '../ImageWithFallback'
import DefaultImage from '@/public/images/v2/default/basic.png'
import Youtube from '@/public/images/youtube.png'
import Soop from '@/public/images/soop.png'
import Twitch from '@/public/images/twitch.png'
import Chzzk from '@/public/images/chzzk.png'
import Image from 'next/image'
type Props = {
  id: string
  filters?: IFilter.IFilterRO
  enableDate?: boolean
  channelType: ChannelType
}

export type SelectedFilters = {
  [key: string]: (string | number)[]
}

export const ChartFilterSheet = ({
  id,
  filters,
  enableDate,
  channelType,
  translations,
}: WithT<Props>) => {
  const t = createT(translations)
  const searchParams = useSearchParams()
  const selectedQueryValue = searchParams.get(id)

  const [selected, setSelected] = useState<SelectedFilters>({})
  const dialog = useDialogStore()
  const { replaceQuery } = useReplaceQuery()

  const isShow = dialog.has(id)

  const buildQueryObject = useCallback(() => {
    return JSON.parse(selectedQueryValue || '{}')
  }, [selectedQueryValue])

  const buildQueryValue = useCallback(() => {
    return JSON.stringify({ ...selected, key: Math.random() })
  }, [selected])

  useEffect(() => {
    if (isShow) {
      setSelected(buildQueryObject())
    } else {
      dialog.push('loading')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, id])

  useEffect(() => {
    dialog.remove('loading')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQueryValue])

  const onClickButton = (
    filter: IFilter.IFilterRow,
    itemKey: string | number,
  ) => {
    const filterKey = filter.key

    setSelected((prev) =>
      produce(prev, (draft) => {
        const index = draft[filterKey]?.indexOf(itemKey)

        if (filter.type === FilterType.SINGLE) {
          draft[filterKey] = [itemKey]
        }

        if (filter.type === FilterType.MUTILPLE) {
          if (typeof index === 'undefined') {
            draft[filterKey] = [itemKey]
          } else if (index === -1) {
            draft[filterKey].push(itemKey)
          } else {
            draft[filterKey].splice(index, 1)

            if (draft[filterKey].length === 0) {
              delete draft[filterKey]
            }
          }
        }

        if (filterKey === 'order' && itemKey === 'TOTAL') {
          if (!selected['date']) {
            onClickDateKey('DAILY')
          }
        }
      }),
    )
  }

  const onClickAll = (filterKey: string) => {
    setSelected((prev) =>
      produce(prev, (draft) => {
        delete draft[filterKey]
      }),
    )
  }

  const onClickButtonSingle = (filterKey: string, itemKey: string | number) => {
    setSelected((prev) =>
      produce(prev, (draft) => {
        const index = draft[filterKey]?.indexOf(itemKey)

        draft[filterKey] = []

        if (typeof index === 'undefined') {
          draft[filterKey].push(itemKey)
        } else if (index === -1) {
          draft[filterKey].push(itemKey)
        } else {
          draft[filterKey].splice(index, 1)

          if (draft[filterKey].length === 0) {
            delete draft[filterKey]
          }
        }
      }),
    )
  }

  const onClickDateKey = (key: string) => {
    if (key === 'TOTAL') {
      onClickAll('date')
      onClickAll('date-val')
    } else {
      onClickButtonSingle('date', key)
      onClickAll('date-val')
      onClickButtonSingle('date-val', dayjs().format('YYYY-MM-DD'))
    }
  }

  const onChangeDate = (date: Date) => {
    onClickButtonSingle('date-val', dayjs(date).format('YYYY-MM-DD'))
  }

  const dateFilter = useMemo(() => {
    const filters = [
      {
        key: 'DAILY',
        label: t('daily'),
      },
      {
        key: 'MONTHLY',
        label: t('monthly'),
      },
      {
        key: 'YEARLY',
        label: t('yearly'),
      },
    ]

    if (channelType === ChannelType.YOUTUBE) {
      filters.push({
        key: 'TOTAL',
        label: t('all'),
      })
    }
    return filters

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelType])

  const dateKey = selected['date']?.[0]

  const selectedDate = useMemo(() => {
    if (!selected['date-val']?.[0]) {
      return new Date()
    }

    return dayjs(selected['date-val']?.[0]).toDate()
  }, [selected])

  const dateFormat = useCallback(
    (val?: Date) => {
      const key = dateKey
      const year = dayjs(val).format('YYYY')
      const month = dayjs(val).format('MM')
      const day = dayjs(val).format('DD')
      if (key === 'YEARLY') {
        return year
      }

      if (key === 'MONTHLY') {
        return `${year}${t('year')} ${month}${t('month')}`
      }

      return `${year}${t('year')} ${month}${t('month')} ${day}${t('day')}`
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateKey],
  )

  const onChangeYear = (acc: number) => {
    const date = new Date(selectedDate)
    date.setFullYear(date.getFullYear() + acc)

    if (date.getFullYear() > new Date().getFullYear()) {
      date.setFullYear(new Date().getFullYear())
    }

    if (date.getFullYear() < 2023) {
      return
    }

    onClickButtonSingle('date-val', dayjs(date).format('YYYY-MM-DD'))
  }

  const minDate = useMemo(() => {
    return new Date('2023-10-07')
  }, [])

  return (
    <FullScreenBackdrop
      token={id}
      transition="sheet"
      onClose={() => replaceQuery(id, buildQueryValue())}
    >
      <div className="divide-y divide-gray-400">
        {filters?.rows.map((f) => (
          <div key={f.key} className="flex flex-col gap-4 py-4">
            <div className="text-key font-bold text-subtitle2">
              {t(f.label)}
            </div>
            <div className="flex gap-3 flex-wrap">
              {f.type === FilterType.MUTILPLE && (
                <button
                  onClick={() => onClickAll(f.key)}
                  className={classNames(
                    'border-gray-400 rounded-lg border p-2.5 text-body2 font-medium flex items-center gap-1',
                    {
                      'bg-text-100 border-text-100 text-gray-0':
                        !selected[f.key],
                    },
                  )}
                >
                  <span>{t('all')}</span>
                </button>
              )}
              {f.items.map((b) => {
                const isIconVisible = f.key === 'channelTypes'

                const icon = {
                  YOUTUBE: (
                    <Image src={Youtube} width={16} height={16} alt="" />
                  ),
                  CHZZK: <Image src={Chzzk} width={16} height={16} alt="" />,
                  AFREECATV: <Image src={Soop} width={16} height={16} alt="" />,
                  TWITCH: <Image src={Twitch} width={16} height={16} alt="" />,
                }

                return (
                  <button
                    key={b.key}
                    className={classNames(
                      'border-gray-400 rounded-lg border p-2.5 text-body2 font-medium flex items-center gap-1',
                      {
                        'bg-text-100 border-text-100 text-gray-0': !selected[
                          f.key
                        ]?.length
                          ? f.default === b.key
                          : selected[f.key]?.includes(b.key),
                      },
                    )}
                    onClick={() => onClickButton(f, b.key)}
                  >
                    {isIconVisible
                      ? icon[b.key as keyof typeof icon]
                      : b.image && (
                          <ImageWithFallback
                            src={b.image.thumbnail || DefaultImage}
                            width={16}
                            height={16}
                            alt={b.label}
                          />
                        )}

                    <span>{t(b.label)}</span>
                  </button>
                )
              })}
            </div>
          </div>
        ))}
        {enableDate && (
          <div className="flex flex-col gap-4 py-4">
            <div className="text-key font-bold text-subtitle2">{t('term')}</div>
            <div className="flex gap-3 flex-wrap">
              {dateFilter.map((f, i) => {
                if (
                  selected['order']?.[selected['order'].length - 1] ===
                    'TOTAL' &&
                  f.key === 'TOTAL'
                ) {
                  return null
                }
                return (
                  <button
                    key={f.key}
                    className={classNames(
                      'border-gray-400 rounded-lg border p-2.5 text-body2 font-medium flex items-center gap-1',
                      {
                        'bg-text-100 border-text-100 text-gray-0': selected[
                          'date'
                        ]
                          ? selected['date'].includes(f.key)
                          : i === 3,
                      },
                    )}
                    onClick={() => onClickDateKey(f.key)}
                  >
                    <span>{f.label}</span>
                  </button>
                )
              })}
            </div>
            {selected.date && (
              <DateInput
                disableAfterToday
                translations={translations}
                id="chart-filter-date"
                value={selectedDate}
                onChange={(d) => onChangeDate(d)}
                selectType={dateKey.toString().toLowerCase() as DateSelectType}
                min={minDate}
              >
                {(val) => (
                  <div className="border-gray-400 rounded-lg border p-2.5 text-body2 font-medium flex items-center justify-center">
                    {dateKey === 'YEARLY' && (
                      <button
                        className="mr-auto px-4"
                        onClick={() => onChangeYear(-1)}
                      >
                        <Icon
                          name="chevron-left"
                          size={18}
                          className="!text-gray-700"
                        />
                      </button>
                    )}
                    {dateFormat(val)}
                    {dateKey === 'YEARLY' && (
                      <button
                        className="ml-auto px-4"
                        onClick={() => onChangeYear(1)}
                      >
                        <Icon
                          name="chevron-right"
                          size={18}
                          className="!text-gray-700"
                        />
                      </button>
                    )}
                  </div>
                )}
              </DateInput>
            )}
          </div>
        )}
      </div>
    </FullScreenBackdrop>
  )
}
